import React from 'react';
import { Avatar, Button, Dialog, DialogContent, Typography, PaletteColor, DialogTitle, DialogActions, Stack, Box, CircularProgress, useTheme } from '@mui/material';
import { CloseDialogAction } from './CloseDialogAction';

interface ConfirmationDialogProps {
  open: boolean;
  icon: React.ReactElement;
  variant?: 'bad' | 'primary';
  iconBgColor?: PaletteColor;
  iconColor?: PaletteColor;
  title: string;
  message: string;
  messageContent?: React.ReactNode;
  confirmButtonText: string;
  cancelButtonText: string;
  isDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  isProcessing?: boolean;
  showConfirm?: boolean;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  icon,
  variant = 'bad',
  iconBgColor,
  iconColor,
  title,
  message,
  messageContent,
  confirmButtonText,
  cancelButtonText,
  isDisabled,
  onConfirm,
  onCancel,
  isProcessing,
  showConfirm,
}) => {
  const theme = useTheme();

  const resolvedIconBgColor = iconBgColor ?? (variant === 'bad' ? theme.palette.error : theme.palette.primary);
  const resolvedIconColor = iconColor ?? (variant === 'bad' ? theme.palette.error : theme.palette.primary);
  const willShowConfirm = (showConfirm === true) || (showConfirm === undefined);

  return <Dialog
    fullWidth
    maxWidth='sm'
    open={open}
    aria-labelledby="alert-dialog-title"
    aria-describedby="alert-dialog-description"
    sx={{
      '& .MuiDialog-container': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    }}
  >
    <DialogTitle
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Avatar
          sx={{
            backgroundColor: resolvedIconBgColor[100],
            color: resolvedIconColor[700],
          }}
        >
          {icon}
        </Avatar>
      </Box>

      <CloseDialogAction onCancel={onCancel}
        isProcessing={isProcessing} />
    </DialogTitle>

    <DialogContent>
      <Stack>
        <Typography variant='textLG'
          fontWeight={600}>{title}</Typography>
        <Typography color="textSecondary"
          sx={{ mt: 1 }}
          variant="textSM">
          {message}
        </Typography>
        {messageContent}
      </Stack>
    </DialogContent>
    <DialogActions>
      <Stack direction={{ xs: 'column', md: 'row' }}
        gap='6px'
        width='100%'>
        <Button
          fullWidth
          variant="secondary"
          size="md"
          disabled={isProcessing}
          onClick={onCancel}>
          {cancelButtonText}
        </Button>
        {willShowConfirm && <Button
          fullWidth
          variant={variant}
          size="md"
          onClick={onConfirm}
          disabled={isProcessing}
          startIcon={isProcessing && <CircularProgress />}
        >
          {confirmButtonText}
        </Button>}
      </Stack>
    </DialogActions>
  </Dialog >;
};
